import type { RouteHandle } from "@packages/core-client/registration/layoutHandles.ts";
import { useSession } from "@packages/core-client/session/SessionContext.tsx";
import { useFeatureFlag } from "@squide/firefly";
import { useMixpanelClient } from "@workleap/telemetry/react";
import { parseAsString, useQueryStates } from "nuqs";
import { useEffect, useMemo, useRef } from "react";
import { useLocation, useMatches } from "react-router";
import { takeUtmParameters, UTM_PARAMETER_KEYS } from "./utmParameters.ts";

const UTM_QUERY_SCHEMA = Object.fromEntries(UTM_PARAMETER_KEYS.map(key => [key, parseAsString]));

const PARAM_PROPERTY_MAP: Readonly<Record<string, string>> = {
    personId: "ViewedMemberId",
    cycleId: "CycleId",
    reviewCycleId: "ReviewCycleId",
    revieweeId: "RevieweeId",
    recognitionId: "RecognitionId",
    feedbackId: "FeedbackId",
    entryId: "TimelineEntryId",
    goalId: "GoalId",
    metricId: "MetricId"
};

interface ResolvedRoute {
    page: string | undefined;
    params: Record<string, string>;
}

function matchKey(match: { readonly id: string; readonly pathname: string }): string {
    return `${match.id}|${match.pathname}`;
}

function resolveRoute(matches: ReturnType<typeof useMatches>): ResolvedRoute {
    let page: string | undefined;

    for (let i = matches.length - 1; i >= 0; i--) {
        const analytics = (matches[i]?.handle as RouteHandle | undefined)?.analytics;

        if (analytics?.page) {
            page = analytics.page;
            break;
        }
    }

    const params: Record<string, string> = {};

    for (const match of matches) {
        for (const [key, value] of Object.entries(match.params ?? {})) {
            if (value !== undefined) {
                params[key] = value;
            }
        }
    }

    return { page, params };
}

function derivePropertiesFromParams(params: Record<string, string>, viewerMemberId: string | undefined): Record<string, unknown> {
    const properties: Record<string, unknown> = {};

    for (const [paramName, propertyName] of Object.entries(PARAM_PROPERTY_MAP)) {
        const value = params[paramName];

        if (value !== undefined) {
            properties[propertyName] = value;
        }
    }

    if (params.personId !== undefined && viewerMemberId !== undefined) {
        properties.IsSelf = params.personId.toLowerCase() === viewerMemberId.toLowerCase();
    }

    return properties;
}

export function PageViewTracker() {
    const mixpanelClient = useMixpanelClient({ throwOnUndefined: false });
    const track = useMemo(() => mixpanelClient?.createTrackingFunction(), [mixpanelClient]);

    const { pathname } = useLocation();
    const matches = useMatches();
    const session = useSession();
    const isUtmTrackingEnabled = useFeatureFlag("utm-tracking", false);
    const [, setUtmQueryStates] = useQueryStates(UTM_QUERY_SCHEMA, { history: "replace" });

    const { page, params } = useMemo(() => resolveRoute(matches), [matches]);

    const lastTrackedPathnameRef = useRef<string | undefined>(undefined);
    const previousMatchKeysRef = useRef<Set<string>>(new Set());
    const hasTrackedLandingRef = useRef(false);

    useEffect(() => {
        if (!track) {
            return;
        }

        if (!page) {
            return;
        }

        const previousPathname = lastTrackedPathnameRef.current;

        if (previousPathname === pathname) {
            return;
        }

        const currentDeepest = matches[matches.length - 1];
        const isReturnToActiveRoute = currentDeepest !== undefined && previousMatchKeysRef.current.has(matchKey(currentDeepest));

        lastTrackedPathnameRef.current = pathname;
        previousMatchKeysRef.current = new Set(matches.map(matchKey));

        if (isReturnToActiveRoute) {
            return;
        }

        const isLanding = session !== null && !hasTrackedLandingRef.current;

        if (isLanding) {
            hasTrackedLandingRef.current = true;
        }

        const utmParameters = isLanding ? takeUtmParameters() : undefined;

        void track("Page Viewed", {
            "Product Identifier": "wln",
            "Page Name": page,
            Path: pathname,
            ...derivePropertiesFromParams(params, session?.memberId),
            ...(isUtmTrackingEnabled ? utmParameters : undefined)
        });

        if (isUtmTrackingEnabled && utmParameters) {
            void setUtmQueryStates(null);
        }
    }, [pathname, page, params, session, track, matches, isUtmTrackingEnabled, setUtmQueryStates]);

    return null;
}
